"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useCallback, useContext, useMemo } from "react";
import type { CreateDropConfig } from "@/entities/IDrop";
import CreateDropStormParts from "./CreateDropStormParts";
import { AnimatePresence, motion } from "framer-motion";
import CreateDropContent from "./CreateDropContent";
import CreateCurationDropContent from "./CreateCurationDropContent";
import { useMutation } from "@tanstack/react-query";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ReactQueryWrapperContext } from "../react-query-wrapper/ReactQueryWrapper";
import { commonApiPost } from "@/services/api/common-api";
import type { ApiCreateDropRequest } from "@/generated/models/ApiCreateDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { AuthContext } from "../auth/Auth";
import { useKeyPressEvent } from "react-use";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { CurationComposerVariant } from "./dropComposer.types";
import { DropMode } from "./dropComposer.types";
import type { DropPrivileges } from "@/hooks/useDropPriviledges";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useUnreadDividerOptional } from "@/contexts/wave/UnreadDividerContext";
import { useWave } from "@/hooks/useWave";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
interface CreateDropProps {
  readonly activeDrop: ActiveDropState | null;
  readonly onCancelReplyQuote: () => void;
  readonly onDropAddedToQueue: () => void;
  readonly onAllDropsAdded?: (() => void) | undefined;
  readonly onExitFixedDropMode?: (() => void) | undefined;
  readonly wave: ApiWave;
  readonly dropId: string | null;
  readonly fixedDropMode: DropMode;
  readonly privileges: DropPrivileges;
  readonly curationComposerVariant?: CurationComposerVariant | undefined;
}
export interface DropMutationBody {
  readonly drop: ApiCreateDropRequest;
  readonly dropId: string | null;
  readonly onSuccess?: (() => void) | undefined;
  readonly onError?: ((error: unknown) => void) | undefined;
}
const ANIMATION_DURATION = 0.3;
export default function CreateDrop(t0) {
  const $ = _c(84);
  const {
    activeDrop,
    onCancelReplyQuote,
    onDropAddedToQueue,
    onAllDropsAdded,
    onExitFixedDropMode,
    wave,
    dropId,
    fixedDropMode,
    privileges,
    curationComposerVariant: t1
  } = t0;
  const curationComposerVariant = t1 === undefined ? "default" : t1;
  const {
    setToast
  } = useContext(AuthContext);
  const {
    waitAndInvalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const unreadDividerContext = useUnreadDividerOptional();
  let t2;
  if ($[0] !== onCancelReplyQuote) {
    t2 = () => onCancelReplyQuote();
    $[0] = onCancelReplyQuote;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useKeyPressEvent("Escape", t2);
  const [isStormMode, setIsStormMode] = useState(false);
  const [drop, setDrop] = useState(null);
  const [dropModeOverride, setDropModeOverride] = useState(null);
  const [curationPrefillSeed, setCurationPrefillSeed] = useState(null);
  const {
    processDropRemoved,
    processIncomingDrop
  } = useMyStream();
  const {
    isMemesWave,
    isCurationWave
  } = useWave(wave);
  const t3 = wave.participation.submission_strategy ?? null;
  let t4;
  if ($[2] !== isCurationWave || $[3] !== isMemesWave || $[4] !== t3) {
    t4 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      submissionStrategy: t3
    });
    $[2] = isCurationWave;
    $[3] = isMemesWave;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const submissionExperience = t4;
  let t5;
  if ($[6] !== activeDrop || $[7] !== fixedDropMode || $[8] !== wave.chat.authenticated_user_eligible || $[9] !== wave.participation.authenticated_user_eligible) {
    t5 = () => {
      if (fixedDropMode === DropMode.CHAT) {
        return false;
      }
      if (fixedDropMode === DropMode.PARTICIPATION) {
        return true;
      }
      if (wave.chat.authenticated_user_eligible) {
        return false;
      }
      if (wave.participation.authenticated_user_eligible) {
        return true;
      }
      if (activeDrop) {
        return false;
      }
      return false;
    };
    $[6] = activeDrop;
    $[7] = fixedDropMode;
    $[8] = wave.chat.authenticated_user_eligible;
    $[9] = wave.participation.authenticated_user_eligible;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const getDefaultIsDropMode = t5;
  const activeDropScope = activeDrop === null ? "none" : `${activeDrop.action}:${activeDrop.drop.id}:${activeDrop.partId}`;
  const modeScopeKey = `${wave.id}:${fixedDropMode}:${wave.chat.authenticated_user_eligible}:${wave.participation.authenticated_user_eligible}:${activeDropScope}`;
  const modeScopeToken = modeScopeKey;
  const defaultIsDropMode = getDefaultIsDropMode();
  const isDropMode = dropModeOverride?.scopeKey === modeScopeToken ? dropModeOverride.value : defaultIsDropMode;
  const initialCurationUrl = curationPrefillSeed?.scopeKey === modeScopeToken ? curationPrefillSeed.url : null;
  const isCurationDropMode = submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && isDropMode;
  let t6;
  if ($[11] !== fixedDropMode || $[12] !== setToast || $[13] !== wave.chat.authenticated_user_eligible || $[14] !== wave.participation.authenticated_user_eligible) {
    t6 = newIsDropMode => {
      if (fixedDropMode !== DropMode.BOTH) {
        return false;
      }
      if (newIsDropMode && !wave.participation.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to drop in this wave",
          type: "error"
        });
        return false;
      }
      if (!newIsDropMode && !wave.chat.authenticated_user_eligible) {
        setToast({
          message: "You are not eligible to chat in this wave",
          type: "error"
        });
        return false;
      }
      return true;
    };
    $[11] = fixedDropMode;
    $[12] = setToast;
    $[13] = wave.chat.authenticated_user_eligible;
    $[14] = wave.participation.authenticated_user_eligible;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const canSwitchDropMode = t6;
  let t7;
  if ($[16] !== canSwitchDropMode || $[17] !== fixedDropMode || $[18] !== modeScopeToken || $[19] !== onExitFixedDropMode) {
    t7 = newIsDropMode_0 => {
      if (!newIsDropMode_0 && fixedDropMode === DropMode.PARTICIPATION && onExitFixedDropMode) {
        setCurationPrefillSeed(null);
        onExitFixedDropMode();
        return;
      }
      if (!canSwitchDropMode(newIsDropMode_0)) {
        return;
      }
      setCurationPrefillSeed(null);
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: newIsDropMode_0
      });
    };
    $[16] = canSwitchDropMode;
    $[17] = fixedDropMode;
    $[18] = modeScopeToken;
    $[19] = onExitFixedDropMode;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const onDropModeChange = t7;
  let t8;
  if ($[21] !== canSwitchDropMode || $[22] !== modeScopeToken) {
    t8 = url => {
      if (!canSwitchDropMode(true)) {
        return;
      }
      setCurationPrefillSeed({
        scopeKey: modeScopeToken,
        url
      });
      setDropModeOverride({
        scopeKey: modeScopeToken,
        value: true
      });
    };
    $[21] = canSwitchDropMode;
    $[22] = modeScopeToken;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const onSwitchToDropModeWithUrl = t8;
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = partIndex => {
      setDrop(prevDrop => {
        if (!prevDrop) {
          return null;
        }
        const newParts = prevDrop.parts.filter((_, i) => i !== partIndex);
        return {
          ...prevDrop,
          parts: newParts,
          referenced_nfts: prevDrop.referenced_nfts,
          mentioned_users: prevDrop.mentioned_users,
          mentioned_waves: prevDrop.mentioned_waves ?? [],
          metadata: prevDrop.metadata
        };
      });
    };
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const onRemovePart = t9;
  let t10;
  if ($[25] !== curationComposerVariant || $[26] !== processDropRemoved || $[27] !== processIncomingDrop || $[28] !== setToast || $[29] !== submissionExperience) {
    t10 = (serverDrop, body_0) => {
      if (body_0.dropId) {
        processDropRemoved(body_0.drop.wave_id, body_0.dropId);
      }
      processIncomingDrop(serverDrop, ProcessIncomingDropType.DROP_INSERT);
      body_0.onSuccess?.();
      if (submissionExperience === WaveSubmissionExperience.CURATION_LEGACY && curationComposerVariant === "leaderboard") {
        setToast({
          message: "Drop submitted successfully",
          type: "success"
        });
      }
    };
    $[25] = curationComposerVariant;
    $[26] = processDropRemoved;
    $[27] = processIncomingDrop;
    $[28] = setToast;
    $[29] = submissionExperience;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== processDropRemoved || $[32] !== setToast) {
    t11 = (error, body_1) => {
      setTimeout(() => {
        if (body_1.dropId) {
          processDropRemoved(body_1.drop.wave_id, body_1.dropId);
        }
      }, 0);
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
      body_1.onError?.(error);
    };
    $[31] = processDropRemoved;
    $[32] = setToast;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11) {
    t12 = {
      mutationFn: _temp,
      onSuccess: t10,
      onError: t11,
      retry: _temp2,
      retryDelay: _temp3
    };
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const addDropMutation = useMutation(t12);
  let t13;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [];
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const queueRef = useRef(t13);
  const isProcessingRef = useRef(false);
  const hasBatchErrorsRef = useRef(false);
  const inFlightProcessNextDropRef = useRef(null);
  let t14;
  if ($[38] !== addDropMutation || $[39] !== onAllDropsAdded || $[40] !== waitAndInvalidateDrops) {
    t14 = async () => {
      if (isProcessingRef.current || queueRef.current.length === 0) {
        return;
      }
      isProcessingRef.current = true;
      while (queueRef.current.length > 0) {
        const dropRequest = queueRef.current.shift();
        if (dropRequest === undefined) {
          break;
        }
        ;
        try {
          await addDropMutation.mutateAsync(dropRequest);
        } catch (t15) {
          const error_0 = t15;
          hasBatchErrorsRef.current = true;
          console.error("Error processing drop:", error_0);
        }
      }
      isProcessingRef.current = false;
      const shouldNotifyAllDropsAdded = !hasBatchErrorsRef.current;
      hasBatchErrorsRef.current = false;
      waitAndInvalidateDrops();
      if (shouldNotifyAllDropsAdded) {
        onAllDropsAdded?.();
      }
    };
    $[38] = addDropMutation;
    $[39] = onAllDropsAdded;
    $[40] = waitAndInvalidateDrops;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const processNextDrop = t14;
  let t15;
  if ($[42] !== onDropAddedToQueue || $[43] !== processNextDrop || $[44] !== unreadDividerContext) {
    t15 = dropRequest_0 => {
      queueRef.current.push(dropRequest_0);
      inFlightProcessNextDropRef.current = processNextDrop();
      if (unreadDividerContext) {
        unreadDividerContext.setUnreadDividerSerialNo(null);
      }
      onDropAddedToQueue();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    $[42] = onDropAddedToQueue;
    $[43] = processNextDrop;
    $[44] = unreadDividerContext;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  const submitDrop = t15;
  const hasExitFixedDropMode = onExitFixedDropMode !== undefined;
  const t16 = fixedDropMode === DropMode.BOTH || fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode;
  const t17 = fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode ? "Close create drop" : null;
  const t18 = fixedDropMode === DropMode.BOTH && privileges.chatRestriction === null || fixedDropMode === DropMode.PARTICIPATION && hasExitFixedDropMode;
  let t19;
  if ($[46] !== activeDrop || $[47] !== drop || $[48] !== dropId || $[49] !== isDropMode || $[50] !== isStormMode || $[51] !== onCancelReplyQuote || $[52] !== onDropModeChange || $[53] !== onSwitchToDropModeWithUrl || $[54] !== privileges || $[55] !== submitDrop || $[56] !== t16 || $[57] !== t17 || $[58] !== t18) {
    t19 = {
      activeDrop,
      onCancelReplyQuote,
      drop,
      isStormMode,
      isDropMode,
      dropId,
      setDrop,
      setIsStormMode,
      onDropModeChange,
      onSwitchToDropModeWithUrl,
      submitDrop,
      privileges,
      showDropModeToggle: t16,
      dropModeToggleExitLabel: t17,
      canExitDropMode: t18
    };
    $[46] = activeDrop;
    $[47] = drop;
    $[48] = dropId;
    $[49] = isDropMode;
    $[50] = isStormMode;
    $[51] = onCancelReplyQuote;
    $[52] = onDropModeChange;
    $[53] = onSwitchToDropModeWithUrl;
    $[54] = privileges;
    $[55] = submitDrop;
    $[56] = t16;
    $[57] = t17;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  const createDropContentProps = t19;
  let t20;
  if ($[60] !== drop?.mentioned_users || $[61] !== drop?.mentioned_waves || $[62] !== drop?.parts || $[63] !== drop?.referenced_nfts || $[64] !== isCurationDropMode || $[65] !== isStormMode) {
    t20 = isStormMode && !isCurationDropMode && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: ANIMATION_DURATION
    }}><CreateDropStormParts parts={drop?.parts ?? []} mentionedUsers={drop?.mentioned_users ?? []} mentionedWaves={drop?.mentioned_waves ?? []} referencedNfts={drop?.referenced_nfts ?? []} onRemovePart={onRemovePart} /></motion.div>;
    $[60] = drop?.mentioned_users;
    $[61] = drop?.mentioned_waves;
    $[62] = drop?.parts;
    $[63] = drop?.referenced_nfts;
    $[64] = isCurationDropMode;
    $[65] = isStormMode;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== t20) {
    t21 = <AnimatePresence>{t20}</AnimatePresence>;
    $[67] = t20;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== activeDrop || $[70] !== createDropContentProps || $[71] !== curationComposerVariant || $[72] !== dropId || $[73] !== initialCurationUrl || $[74] !== isCurationDropMode || $[75] !== isDropMode || $[76] !== onCancelReplyQuote || $[77] !== submissionExperience || $[78] !== submitDrop || $[79] !== wave) {
    t22 = isCurationDropMode ? <CreateCurationDropContent activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} wave={wave} dropId={dropId} isDropMode={isDropMode} initialUrl={initialCurationUrl} submitDrop={submitDrop} curationComposerVariant={curationComposerVariant} /> : <CreateDropContent {...createDropContentProps} wave={wave} submissionExperience={submissionExperience} />;
    $[69] = activeDrop;
    $[70] = createDropContentProps;
    $[71] = curationComposerVariant;
    $[72] = dropId;
    $[73] = initialCurationUrl;
    $[74] = isCurationDropMode;
    $[75] = isDropMode;
    $[76] = onCancelReplyQuote;
    $[77] = submissionExperience;
    $[78] = submitDrop;
    $[79] = wave;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] !== t21 || $[82] !== t22) {
    t23 = <>{t21}{t22}</>;
    $[81] = t21;
    $[82] = t22;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  return t23;
}
function _temp3(failureCount_0) {
  return failureCount_0 * 1000;
}
function _temp2(failureCount) {
  return failureCount < 3;
}
async function _temp(body) {
  return commonApiPost({
    endpoint: "drops",
    body: body.drop
  });
}